import React, { useState, useEffect, useCallback } from "react";
import {
    Plus,
    Search,
    Edit2,
    Trash2,
    Globe,
    Lock,
    CheckCircle2,
    Clock,
    Tag,
    Music2,
    ShoppingBag,
    X,
    Save,
    AlertCircle,
    Loader2
} from "lucide-react";
import { useInventoryCategories } from "../../services/admin/InventoryCategoryServices";

export default function CategoriesPage() {
    const {
        fetchAllCategories,
        createCategory,
        updateCategory,
        deleteCategory,
        toggleCategoryStatus,
        loading
    } = useInventoryCategories();

    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({ name: "", type: "Music Content" });

    // Recovery/Optimized States
    const [statusLoading, setStatusLoading] = useState({}); // { id: true/false }
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, category: null });

    const loadCategories = useCallback(async () => {
        const data = await fetchAllCategories();
        setCategories(data);
    }, [fetchAllCategories]);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    const handleOpenModal = (category = null) => {
        if (category) {
            setEditingCategory(category);
            setFormData({ name: category.name, type: category.type });
        } else {
            setEditingCategory(null);
            setFormData({ name: "", type: "Music Content" });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingCategory(null);
        setFormData({ name: "", type: "Music Content" });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        let res;
        if (editingCategory) {
            res = await updateCategory(editingCategory.id, formData);
        } else {
            res = await createCategory(formData);
        }

        if (res.status === "success") {
            handleCloseModal();
            loadCategories();
        }
    };

    const handleDeleteClick = (category) => {
        setDeleteModal({ isOpen: true, category });
    };

    const confirmDelete = async () => {
        const { id } = deleteModal.category;
        const res = await deleteCategory(id);
        if (res.status === "success") {
            setDeleteModal({ isOpen: false, category: null });
            loadCategories();
        }
    };

    const toggleStatus = async (id) => {
        setStatusLoading(prev => ({ ...prev, [id]: true }));
        const res = await toggleCategoryStatus(id);
        if (res.status === "success") {
            await loadCategories();
        }
        setStatusLoading(prev => ({ ...prev, [id]: false }));
    };

    const filteredCategories = (Array.isArray(categories) ? categories : []).filter(cat =>
        (cat.name || '').toLowerCase().includes((searchTerm || '').toLowerCase()) ||
        (cat.type || '').toLowerCase().includes((searchTerm || '').toLowerCase())
    );

    return (
        <>
            <div className="p-6 lg:p-10 max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-normal text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                            Category Management
                        </h1>
                        <p className="text-black dark:text-white/50 text-sm mt-1">
                            Organize your products into Music Content and Artist Merch categories.
                        </p>
                    </div>
                    <button
                        onClick={() => handleOpenModal()}
                        className="flex items-center justify-center gap-2 bg-black text-white px-6 py-3 rounded-xl text-sm font-bold tracking-widest hover:bg-jazz-brown transition-all w-full md:w-auto shadow-lg shadow-black/10"
                    >
                        <Plus size={18} /> CREATE CATEGORY
                    </button>
                </div>

                {/* Filters & Search */}
                <div className="bg-white dark:bg-[#1A1A1A] p-4 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm mb-6 flex flex-col md:flex-row gap-4 items-center">
                    <div className="relative flex-1 w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-black dark:text-white/40" size={18} />
                        <input
                            type="text"
                            placeholder="Search categories..."
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm text-black dark:text-white placeholder-black dark:placeholder-white/40"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Categories Table */}
                <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-black/5 dark:border-white/5 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b border-black/5 dark:border-white/5 bg-[#FAFAFA] dark:bg-[#222]">
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Category Name</th>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Association</th>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest text-center">Items Attached</th>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Status</th>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-black/5 dark:divide-white/5 relative">
                                {loading && categories.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-20 text-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <Loader2 size={32} className="animate-spin text-jazz-brown" />
                                                <p className="text-black/70 dark:text-white/50 text-sm font-medium">Loading categories...</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredCategories.map((category) => (
                                    <tr key={category.id} className="hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-jazz-brown/5 flex items-center justify-center text-jazz-brown">
                                                    <Tag size={16} />
                                                </div>
                                                <span className="font-semibold text-sm text-black dark:text-white">{category.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider ${category.type === "Music Content"
                                                ? "bg-blue-50 text-blue-600 dark:bg-blue-900/10 dark:text-blue-400"
                                                : "bg-purple-50 text-purple-600 dark:bg-purple-900/10 dark:text-purple-400"
                                                }`}>
                                                {category.type === "Music Content" ? <Music2 size={12} /> : <ShoppingBag size={12} />}
                                                {category.type}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className="text-sm font-medium text-black dark:text-white/60 bg-gray-100 dark:bg-white/5 px-2.5 py-1 rounded-md">
                                                {category.itemsCount}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${category.status === 'active'
                                                ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                                : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                                                }`}>
                                                {category.status === 'active' ? <CheckCircle2 size={12} /> : <Clock size={12} />}
                                                {category.status.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end items-center gap-2">
                                                <button
                                                    onClick={() => handleOpenModal(category)}
                                                    className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-lg text-black dark:text-white/60 hover:text-black dark:hover:text-white transition-colors"
                                                    title="Edit"
                                                    disabled={loading}
                                                >
                                                    <Edit2 size={18} />
                                                </button>
                                                <button
                                                    onClick={() => toggleStatus(category.id)}
                                                    className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-lg text-black dark:text-white/60 hover:text-black dark:hover:text-white transition-colors"
                                                    title={category.status === 'active' ? "Disable" : "Enable"}
                                                    disabled={loading || statusLoading[category.id]}
                                                >
                                                    {statusLoading[category.id] ? (
                                                        <Loader2 size={18} className="animate-spin text-jazz-brown" />
                                                    ) : category.status === 'active' ? (
                                                        <Globe size={18} className="text-green-500" />
                                                    ) : (
                                                        <Lock size={18} />
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteClick(category)}
                                                    className="p-2 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg text-red-400 hover:text-red-600 transition-colors"
                                                    title="Delete"
                                                    disabled={loading || statusLoading[category.id]}
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!loading && filteredCategories.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-20 text-center text-black dark:text-white/50 font-medium italic">
                                            No categories found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Create/Edit Category Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleCloseModal} />
                    <div className="relative bg-white dark:bg-[#1A1A1A] w-full max-w-md rounded-3xl shadow-2xl border border-black/5 dark:border-white/5 overflow-hidden">
                        <div className="px-6 py-4 border-b border-black/5 dark:border-white/5 flex items-center justify-between">
                            <h2 className="text-xl font-normal text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                                {editingCategory ? "Update Category" : "New Category"}
                            </h2>
                            <button onClick={handleCloseModal} className="text-black dark:text-white/60 hover:text-black dark:hover:text-white p-2"><X size={20} /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            <div>
                                <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest mb-2">Category Name</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full px-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium"
                                    placeholder="e.g., Vinyl Records"
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest mb-2">Association</label>
                                <div className="grid grid-cols-2 gap-3">
                                    {["Music Content", "Artist Merch"].map((type) => (
                                        <button
                                            key={type}
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, type }))}
                                            className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition-all ${formData.type === type
                                                ? "bg-black text-white border-black"
                                                : "bg-[#FAFAFA] dark:bg-[#222] border-black/5 text-black dark:text-white/60"
                                                }`}
                                        >
                                            {type === "Music Content" ? <Music2 size={24} /> : <ShoppingBag size={24} />}
                                            <span className="text-xs font-bold uppercase tracking-widest">{type}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-black text-white py-4 rounded-xl text-xs font-bold tracking-widest hover:bg-jazz-brown transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <Loader2 size={16} className="animate-spin" />
                                ) : (
                                    <Save size={16} />
                                )}
                                {loading
                                    ? (editingCategory ? "UPDATING..." : "SAVING...")
                                    : (editingCategory ? "UPDATE CATEGORY" : "SAVE CATEGORY")}
                            </button>
                        </form>
                    </div>
                </div>
            )}
            {/* Advanced Deletion Modal */}
            {deleteModal.isOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 overflow-hidden">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteModal({ isOpen: false, category: null })} />
                    <div className="relative bg-white dark:bg-[#1A1A1A] w-full max-w-md rounded-3xl shadow-2xl border border-black/5 dark:border-white/5 overflow-hidden">
                        <div className="p-8 text-center">
                            <div className="w-16 h-16 bg-red-50 dark:bg-red-900/10 rounded-full flex items-center justify-center mx-auto mb-6 text-red-500">
                                <AlertCircle size={32} />
                            </div>

                            {deleteModal.category.itemsCount > 0 ? (
                                <>
                                    <h3 className="text-xl font-normal text-black dark:text-white mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                                        Cannot Delete Category
                                    </h3>
                                    <p className="text-black dark:text-white/50 text-sm mb-8 leading-relaxed">
                                        The category <span className="font-bold text-black dark:text-white">"{deleteModal.category.name}"</span> has <span className="text-jazz-brown font-bold">{deleteModal.category.itemsCount} products</span> attached to it.
                                        <br /><br />
                                        Please <span className="font-semibold italic font-primary">move these items</span> to another category or <span className="font-semibold italic font-primary">disable</span> them before attempting to delete this category.
                                    </p>
                                    <button
                                        onClick={() => setDeleteModal({ isOpen: false, category: null })}
                                        className="w-full bg-black text-white py-4 rounded-xl text-xs font-bold tracking-widest hover:bg-jazz-brown transition-all shadow-lg"
                                    >
                                        UNDERSTOOD
                                    </button>
                                </>
                            ) : (
                                <>
                                    <h3 className="text-xl font-normal text-black dark:text-white mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                                        Confirm Deletion
                                    </h3>
                                    <p className="text-black dark:text-white/50 text-sm mb-8 leading-relaxed">
                                        Are you sure you want to delete <span className="font-bold text-black dark:text-white">"{deleteModal.category.name}"</span>?
                                        <br /><br />
                                        <span className="text-red-500 font-bold uppercase tracking-wider text-xs">Warning:</span> This action cannot be undone and will permanently remove the category.
                                    </p>
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            onClick={() => setDeleteModal({ isOpen: false, category: null })}
                                            className="py-4 rounded-xl border border-black/5 text-black dark:text-white/60 text-xs font-bold tracking-widest hover:bg-gray-50 dark:hover:bg-zinc-800 transition-all font-secondary"
                                        >
                                            CANCEL
                                        </button>
                                        <button
                                            onClick={confirmDelete}
                                            disabled={loading}
                                            className="bg-red-500 text-white py-4 rounded-xl text-xs font-bold tracking-widest hover:bg-red-600 transition-all shadow-lg flex items-center justify-center gap-2"
                                        >
                                            {loading ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />} DELETE
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
